import {
  CATCH_BOARD_CLOSED_STAGES,
  getCatchBoardStageMeta,
} from 'src/screens/catch-board/catchBoardConstants'

/**
 * Dedupe / stringify venue ids for duplicate-deal checks.
 * @param {Array<string|null|undefined>|string|null|undefined} venueIds
 * @returns {string[]}
 */
export function normalizeCatchBoardVenueIds(venueIds = []) {
  const list = Array.isArray(venueIds) ? venueIds : [venueIds]
  return [
    ...new Set(
      list.map(id => String(id || '').trim()).filter(Boolean),
    ),
  ]
}

function getDealId(deal) {
  if (deal?.dealId != null) {
    return String(deal.dealId)
  }
  if (deal?._id != null) {
    return String(deal._id)
  }
  return null
}

function getDealVenueIds(deal = {}) {
  return normalizeCatchBoardVenueIds([
    ...(Array.isArray(deal.linkedVenueIds) ? deal.linkedVenueIds : []),
    deal.primaryVenueId,
  ])
}

/**
 * Detect other open Catch Board deals that share any of the given venue ids.
 * Closed stages and soft-deleted deals are ignored. Pure — no network.
 *
 * @param {{
 *   venueIds?: Array<string|null|undefined>|string|null,
 *   deals?: Array<object>,
 *   excludeDealId?: string|null,
 * }} [options]
 * @returns {Array<{ venueId: string, deals: Array<{ dealId: string, name: string, stage: string|null }> }>}
 */
export function buildCatchBoardDuplicateVenueWarnings({
  venueIds = [],
  deals = [],
  excludeDealId = null,
} = {}) {
  const targetVenueIds = normalizeCatchBoardVenueIds(venueIds)
  if (!targetVenueIds.length) {
    return []
  }

  const excludeId = excludeDealId ? String(excludeDealId) : null
  const targetSet = new Set(targetVenueIds)
  const warningsByVenue = new Map()

  for (const deal of deals || []) {
    const dealId = getDealId(deal)
    if (!dealId || (excludeId && dealId === excludeId)) {
      continue
    }
    if (deal?.deletedAt) {
      continue
    }
    if (CATCH_BOARD_CLOSED_STAGES.includes(deal?.stage)) {
      continue
    }

    for (const venueId of getDealVenueIds(deal)) {
      if (!targetSet.has(venueId)) {
        continue
      }

      if (!warningsByVenue.has(venueId)) {
        warningsByVenue.set(venueId, [])
      }

      const bucket = warningsByVenue.get(venueId)
      if (bucket.some(row => row.dealId === dealId)) {
        continue
      }

      bucket.push({
        dealId,
        name: deal.name || '',
        stage: deal.stage || null,
      })
    }
  }

  return targetVenueIds
    .filter(venueId => warningsByVenue.has(venueId))
    .map(venueId => ({
      venueId,
      deals: warningsByVenue.get(venueId),
    }))
}

/**
 * @param {Array<{ deals?: Array<object> }>} [warnings]
 * @returns {boolean}
 */
export function hasCatchBoardDuplicateVenueWarnings(warnings = []) {
  return (
    Array.isArray(warnings) &&
    warnings.some(warning => Array.isArray(warning?.deals) && warning.deals.length > 0)
  )
}

/**
 * Human-readable line for alerts / handoff copy.
 * Example: "Bonnie Burrito: Pro upgrade (Nibbles), Test Deal (Reeling In)"
 *
 * @param {{ venueId?: string, deals?: Array<{ name?: string, stage?: string|null }> }} warning
 * @param {{ venueNamesById?: Record<string, string> }} [options]
 * @returns {string}
 */
export function formatCatchBoardDuplicateVenueWarning(
  warning,
  { venueNamesById = {} } = {},
) {
  const venueId = warning?.venueId ? String(warning.venueId) : ''
  const venueName =
    (venueId && venueNamesById[venueId]) ||
    (venueId ? `Venue ${venueId}` : 'Venue')

  const deals = (warning?.deals || [])
    .map(deal => {
      const meta = getCatchBoardStageMeta(deal.stage)
      const stageLabel = meta?.label || deal.stage || 'open'
      return `${deal.name || 'Deal'} (${stageLabel})`
    })
    .join(', ')

  return deals
    ? `${venueName}: ${deals}`
    : `${venueName}: also linked elsewhere`
}

/**
 * Compact sales copy: "also on: Pro upgrade (Nibbles)".
 *
 * @param {{ deals?: Array<{ name?: string, stage?: string|null }> }} warning
 * @returns {string}
 */
export function formatCatchBoardDuplicateVenueAlsoOn(warning) {
  const deals = (warning?.deals || [])
    .map(deal => {
      const meta = getCatchBoardStageMeta(deal.stage)
      const stageLabel = meta?.label || deal.stage || 'open'
      return `${deal.name || 'Deal'} (${stageLabel})`
    })
    .join(', ')

  return deals ? `also on: ${deals}` : 'also on: another open deal'
}
